import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  Button,
  FieldError,
  Form,
  Input,
  Label,
  ListBox,
  Modal,
  Select,
  Switch,
  TextField,
} from "@heroui/react";
import { Icon } from "@iconify/react";

import {
  chargeTypeTitles,
  PackageOptionChargeType,
  PackageOptionEntity,
} from "@/features/package-options/type.ts";
import {
  PackageOptionPayload,
  useCreatePackageOption,
  useEditPackageOption,
} from "@/features/package-options/use-package-options.ts";

interface IFormInput {
  name: string;
  type: string;
  price: string;
  chargeType: PackageOptionChargeType;
  dimensions: string;
  description: string;
  sortOrder: string;
  isActive: boolean;
}

const chargeTypes = (
  Object.keys(chargeTypeTitles) as PackageOptionChargeType[]
).map((key) => ({ key, title: chargeTypeTitles[key] }));

/** Создание опции, если option не передан, иначе редактирование. */
export function PackageOptionModal({
  option,
}: {
  option?: PackageOptionEntity;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const create = useCreatePackageOption();
  const edit = useEditPackageOption();
  const isPending = create.isPending || edit.isPending;

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IFormInput>();

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    const payload: PackageOptionPayload = {
      name: data.name.trim(),
      type: data.type.trim().toUpperCase(),
      price: Number(data.price),
      chargeType: data.chargeType,
      dimensions: data.dimensions?.trim() ?? "",
      description: data.description?.trim() ?? "",
      isActive: data.isActive,
      sortOrder: Number(data.sortOrder ?? 0),
    };

    const request = option
      ? edit.mutateAsync({ id: option.id, ...payload })
      : create.mutateAsync(payload);

    request.then(() => {
      reset();
      setIsOpen(false);
    });
  };

  return (
    <div>
      {option ? (
        <Button isIconOnly variant="ghost" onPress={() => setIsOpen(true)}>
          <Icon className="size-6" icon="solar:pen-bold-duotone" />
        </Button>
      ) : (
        <Button variant="primary" onPress={() => setIsOpen(true)}>
          Создать
          <Icon className="size-6" icon="solar:add-circle-bold" />
        </Button>
      )}

      <Modal isOpen={isOpen} onOpenChange={setIsOpen}>
        <Modal.Backdrop isDismissable>
          <Modal.Container placement="center">
            <Modal.Dialog>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Header>
                  <Modal.Heading>
                    {option ? "Изменить опцию" : "Новая опция"}
                  </Modal.Heading>
                </Modal.Header>

                <Modal.Body>
                  <TextField
                    defaultValue={option?.name}
                    isInvalid={!!errors.name?.message}
                  >
                    <Label>Название</Label>
                    <Input
                      {...register("name", {
                        required: "Нужно заполнить поле",
                      })}
                    />
                    <FieldError>{errors.name?.message}</FieldError>
                  </TextField>

                  <TextField
                    defaultValue={option?.type}
                    isInvalid={!!errors.type?.message}
                  >
                    <Label>Код (латиницей, например BOX_F1)</Label>
                    <Input
                      {...register("type", {
                        required: "Нужно заполнить поле",
                        pattern: {
                          value: /^[A-Za-z0-9_]+$/,
                          message: "Только латиница, цифры и _",
                        },
                      })}
                    />
                    <FieldError>{errors.type?.message}</FieldError>
                  </TextField>

                  <Controller
                    control={control}
                    defaultValue={option?.chargeType ?? "fixed"}
                    name="chargeType"
                    render={({ field }) => (
                      <Select
                        isInvalid={!!errors.chargeType?.message}
                        selectedKey={field.value ?? null}
                        onSelectionChange={(key) => field.onChange(key)}
                      >
                        <Label>Начисление</Label>
                        <Select.Trigger>
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <FieldError>{errors.chargeType?.message}</FieldError>
                        <Select.Popover>
                          <ListBox items={chargeTypes}>
                            {({ key, title }) => (
                              <ListBox.Item id={key} textValue={title}>
                                {title}
                              </ListBox.Item>
                            )}
                          </ListBox>
                        </Select.Popover>
                      </Select>
                    )}
                    rules={{ required: "Нужно заполнить поле" }}
                  />

                  <TextField
                    defaultValue={option ? `${option.price}` : "0"}
                    isInvalid={!!errors.price?.message}
                  >
                    <Label>Цена (сумма в ₸ или процент)</Label>
                    <Input
                      {...register("price", {
                        required: "Нужно заполнить поле",
                        min: {
                          value: 0,
                          message: "Цена не может быть меньше 0",
                        },
                      })}
                    />
                    <FieldError>{errors.price?.message}</FieldError>
                  </TextField>

                  <TextField defaultValue={option?.dimensions}>
                    <Label>Размеры</Label>
                    <Input {...register("dimensions")} />
                  </TextField>

                  <TextField defaultValue={option?.description}>
                    <Label>Примечание</Label>
                    <Input {...register("description")} />
                  </TextField>

                  <TextField
                    defaultValue={option ? `${option.sortOrder}` : "0"}
                  >
                    <Label>Порядок вывода</Label>
                    <Input {...register("sortOrder")} />
                  </TextField>

                  <Controller
                    control={control}
                    defaultValue={option?.isActive ?? true}
                    name="isActive"
                    render={({ field }) => (
                      <Switch
                        isSelected={field.value}
                        onChange={field.onChange}
                      >
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                        <Switch.Content>
                          <Label>Показывать клиенту</Label>
                        </Switch.Content>
                      </Switch>
                    )}
                  />
                </Modal.Body>

                <Modal.Footer>
                  <Button
                    variant="danger-soft"
                    onPress={() => setIsOpen(false)}
                  >
                    Отмена
                  </Button>

                  <Button isPending={isPending} type="submit" variant="primary">
                    Сохранить
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>
    </div>
  );
}
